import React, { useEffect, useRef, useState } from "react";
import "./SupportPopup.css";

const SUPPORT_URL = "https://fairy.hada.io/@tooloves#support";
const HIDDEN_UNTIL_KEY = "tooloves-support-popup-hidden-until";
const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

const messages = {
  ko: {
    eyebrow: "TooLoves 후원 안내",
    title: "사이트 운영을 도와주세요",
    body: "도메인이 30일 이내에 만료될 예정입니다. 후원금은 도메인 및 서버 비용에 사용되며, 충분히 모이지 않으면 서비스가 종료될 수 있습니다. 관심과 후원에 감사드립니다.",
    support: "후원하기",
    close: "닫기",
    closeIcon: "팝업 닫기",
    hideForDay: "하루 동안 보지 않기",
  },
  en: {
    eyebrow: "Support TooLoves",
    title: "Help keep this site online",
    body: "Our domain expires within 30 days. Donations will cover domain renewal and server costs. If we cannot raise enough, the service may close. Thank you for your support.",
    support: "Donate",
    close: "Close",
    closeIcon: "Close popup",
    hideForDay: "Don't show for 24 hours",
  },
  ja: {
    eyebrow: "TooLoves ご支援のお願い",
    title: "サイト運営にご協力ください",
    body: "ドメインの有効期限が30日以内に迫っています。ご支援はドメインとサーバーの費用に使用します。十分な支援が集まらない場合、サービスを終了する可能性があります。ご関心とご支援に感謝いたします。",
    support: "支援する",
    close: "閉じる",
    closeIcon: "ポップアップを閉じる",
    hideForDay: "24時間表示しない",
  },
  tw: {
    eyebrow: "支持 TooLoves",
    title: "協助網站持續營運",
    body: "網域將在30天內到期。贊助款將用於網域續費與伺服器費用。若未能籌得足夠款項，服務可能會停止營運。感謝您的關注與支持。",
    support: "前往贊助",
    close: "關閉",
    closeIcon: "關閉彈出視窗",
    hideForDay: "24小時內不再顯示",
  },
};

function shouldShowPopup() {
  if (typeof window === "undefined") return true;

  try {
    const hiddenUntil = Number(window.localStorage.getItem(HIDDEN_UNTIL_KEY));
    return !hiddenUntil || hiddenUntil <= Date.now();
  } catch {
    return true;
  }
}

function SupportPopup({ locale = "ko" }) {
  const [isOpen, setIsOpen] = useState(shouldShowPopup);
  const closeButtonRef = useRef(null);
  const copy = messages[locale] || messages.ko;

  const hideForOneDay = () => {
    try {
      window.localStorage.setItem(
        HIDDEN_UNTIL_KEY,
        String(Date.now() + ONE_DAY_IN_MS)
      );
    } catch {
      // 저장이 제한된 환경에서도 팝업 닫기는 정상 동작합니다.
    }
    setIsOpen(false);
  };

  useEffect(() => {
    const body = document.body;

    // 이전 사전 렌더링 결과에 남았을 수 있는 인라인 잠금을 정리합니다.
    if (body.style.overflow === "hidden") {
      body.style.removeProperty("overflow");
    }

    if (!isOpen) {
      body.classList.remove("support-popup-open");
      return undefined;
    }

    body.classList.add("support-popup-open");
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      body.classList.remove("support-popup-open");
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="support-popup-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) setIsOpen(false);
      }}
    >
      <section
        className="support-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-popup-title"
        aria-describedby="support-popup-description"
      >
        <button
          ref={closeButtonRef}
          type="button"
          className="support-popup-close"
          onClick={() => setIsOpen(false)}
          aria-label={copy.closeIcon}
        >
          <span aria-hidden="true">×</span>
        </button>

        <div className="support-popup-icon" aria-hidden="true">
          ♥
        </div>
        <p className="support-popup-eyebrow">{copy.eyebrow}</p>
        <h2 id="support-popup-title">{copy.title}</h2>
        <p id="support-popup-description" className="support-popup-description">
          {copy.body}
        </p>

        <a
          className="support-popup-button"
          href={SUPPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          {copy.support}
          <span aria-hidden="true">→</span>
        </a>
        <div className="support-popup-secondary-actions">
          <button type="button" onClick={() => setIsOpen(false)}>
            {copy.close}
          </button>
          <button type="button" onClick={hideForOneDay}>
            {copy.hideForDay}
          </button>
        </div>
      </section>
    </div>
  );
}

export default SupportPopup;
